import { Outlet, useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box, useEffect } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { ProjectSaleInvoicesPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FeatureAvailabilityType } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { SaleInvoicesListTable } from 'modules/finance/components/Invoices/SaleInvoicesListTable'
import { useArchiveSaleInvoiceDialog } from 'modules/finance/hooks/sale_invoices/useArchiveSaleInvoiceDialog'
import EmptyState from 'modules/project_management/components/EmptyState'
import { UpsellProjectInvoices } from 'modules/project_management/components/upsells/UpsellProjectInvoices'

import { ProjectDetailsPageSaleInvoicesPageInsights } from './Insights'

const ProjectSaleInvoicesPage = () => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { access, company, employee: currentEmployee } = useCurrentSession()
  const locale = access.locale
  const { showArchiveSaleInvoiceDialog } = useArchiveSaleInvoiceDialog()
  const { id } = useParams()
  const projectId = Number(id)

  useEffect(() => {
    track(trackingEvents.projectManagement.salesInvoices.viewInvoicesTab)
  }, [])

  const { data } = useQuery(ProjectSaleInvoicesPageDocument, {
    variables: {
      projectId,
      companyId: company.id,
      featureIds: [
        Features.sales_invoices_creation,
        Features.projects_costs,
        Features.sales_invoices_management,
      ],
    },
  })

  const createInvoicesFeatureAvailable =
    data?.finance?._policies?.financialDocumentsCreateSale?.can ?? false

  const availableCurrencies = data
    ? data.apiCore.currenciesConnection.edges.map((edge) => edge.node)
    : []

  const featureUsage: FeatureAvailabilityType = data?.companies.featureAvailabilitiesConnection
    .nodes[0] ?? {
    available: false,
    id: Features.sales_invoices_creation,
    _internalType: 'feature',
  }

  const hasProjectsFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_costs
  )?.available

  const hasSalesInvoicesManagement = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.sales_invoices_management
  )?.available

  const project = data?.projectManagement.project
  const canManageProjectSalesInvoices = project?._policies?.manageProjectInvoices.can ?? false
  const projectCurrency = project?.legalEntity?.currency ?? 'EUR'

  if (!project || !project?._policies?.assignOwner.can) return null

  const usage = featureUsage?.usage || 0
  const usageLimit = featureUsage?.usageLimit || -1

  const isSaleInvoiceDetail = location.pathname.includes('/sale_invoices/detail/')

  return !hasProjectsFeature || !hasSalesInvoicesManagement ? (
    <UpsellProjectInvoices projectsMissing={!hasProjectsFeature} />
  ) : (
    <>
      {!canManageProjectSalesInvoices ? (
        <EmptyState
          title={i18n.t('project_management.sale_invoices.empty_state_no_permissions.title')}
          description={i18n.t(
            'project_management.sale_invoices.empty_state_no_permissions.description'
          )}
          emoji={'✏️'}
        />
      ) : (
        <>
          {!isSaleInvoiceDetail && (
            <>
              <Box paddingBottom="s24">
                <ProjectDetailsPageSaleInvoicesPageInsights
                  projectId={projectId}
                  projectCurrency={projectCurrency}
                />
              </Box>
              <SaleInvoicesListTable
                companyId={company.id}
                locale={locale}
                legalEntitiesOptions={[]}
                availableCurrencies={availableCurrencies}
                showDeleteModalForInvoiceId={(invoiceId) =>
                  showArchiveSaleInvoiceDialog({ invoiceId })
                }
                projectId={projectId}
                onClickAdd={() => {
                  navigate(
                    navigation.projects.id.details.sale_invoices.create.fullPath({ id: projectId })
                  )
                }}
                detailPath={(invoiceId) =>
                  navigation.projects.id.details.sale_invoices.invoiceId.fullPath({
                    id: projectId,
                    invoiceId,
                  })
                }
                onClickExport={() => {
                  track(trackingEvents.accounting.saleInvoices.clickExport)
                  navigate(
                    navigation.projects.id.details.sale_invoices.export.fullPath({
                      id: projectId,
                    })
                  )
                }}
                createInvoicesFeatureAvailable={createInvoicesFeatureAvailable}
                usage={usage}
                usageLimit={usageLimit}
                projectsOrigin
                currentEmployeeId={currentEmployee?.id || -1}
                pageDetailPath={(invoiceId) =>
                  navigation.projects.id.details.sale_invoices.detail.fullPath({
                    id: projectId,
                    invoiceId,
                  })
                }
              />
            </>
          )}
          <Outlet
            context={{
              project,
            }}
          />
        </>
      )}
    </>
  )
}

export default ProjectSaleInvoicesPage
